import { LOCALES, isRtlLocale, type Locale } from './locales';

/** The class engines other than Google's look for; the attribute is the standard. */
export const NO_TRANSLATE_CLASS = 'notranslate';

/**
 * The base language subtag, which is what `lang` carries: `ar`, `en`, `ckb`.
 * `ar-IQ` or `en-US` arriving from a stored choice or a `preferredLocale`
 * collapses to its subtag so the attribute matches the bundle that is actually
 * rendered — and matches what the pre-paint script in `index.html` writes.
 */
export function documentLanguage(locale: string): Locale {
  const base = locale.split('-')[0];
  return (LOCALES as readonly string[]).includes(base) ? (base as Locale) : 'ar';
}

/**
 * Keeps the root element's `lang`, `dir` and translation opt-out in step with
 * the active locale. Called from `AppThemeProvider`, which owns the other
 * document-level consequences of a language change.
 *
 * **The opt-out is re-asserted on every language change, not only in
 * `index.html`.** Browser page translation rewrites text nodes in place, so on
 * a prescription it would silently alter a medicine name, a dose or a usage
 * instruction — and on the printed sheet, what the patient carries to the
 * pharmacy. A truthful `lang` is the other half: a document that claims the
 * wrong language is exactly what makes a browser offer to translate it.
 */
export function applyDocumentLanguage(
  locale: string,
  root: HTMLElement = document.documentElement,
): void {
  const lang = documentLanguage(locale);
  root.lang = lang;
  root.dir = isRtlLocale(lang) ? 'rtl' : 'ltr';
  root.setAttribute('translate', 'no');
  root.classList.add(NO_TRANSLATE_CLASS);
}
